import { useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import type { EmailAttachmentResult, ValidateResult } from '../api/client'
import { isEmailContainer, validateEmail, validateXml, ValidateEmailError } from '../api/client'
import { ValidatorHistoryPanel } from './ValidatorHistoryPanel'
import { ValidatorFileSidebar } from './ValidatorFileSidebar'
import { ValidatorResultDetail } from './ValidatorResultDetail'
import { ValidatorEmailModal } from './ValidatorEmailModal'
import type { EmailTarget } from './ValidatorEmailModal'

type Tab = 'validate' | 'history'

export type FileStatus = 'pending' | 'validating' | 'done' | 'error'

/**
 * Eén item in de multi-file set. `kind: 'email'` is een containermail
 * (.eml/.msg) die nog server-side wordt uitgepakt — na verwerking wordt die
 * vervangen door de gevonden XML/PDF-bijlagen (`kind: 'document'`), of blijft
 * hij staan als foutmelding als er niets bruikbaars in zat.
 */
export interface ValidatorFileEntry {
  id: string
  kind: 'document' | 'email'
  name: string
  size: number
  file?: File
  status: FileStatus
  result?: ValidateResult
  errorMsg?: string
}

const SUPPORTED_EXTENSIONS = ['.xml', '.pdf', '.eml', '.msg']

const BATCH_SIZE = 5

export function ValidatorPage() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<Tab>('validate')

  // Multi-file set
  const [entries, setEntries]     = useState<ValidatorFileEntry[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Overgeslagen (niet-XML/PDF) mailbijlagen in de huidige set
  const [skippedCount, setSkippedCount] = useState(0)
  const [depthLimitReached, setDepthLimitReached] = useState(false)

  // History-geopend resultaat (los van de set)
  const [historyResult, setHistoryResult]     = useState<ValidateResult | null>(null)
  const [historyFilename, setHistoryFilename] = useState<string | null>(null)

  // E-mail selectie (multi-file)
  const [emailSelectedIds, setEmailSelectedIds] = useState<Set<string>>(new Set())
  const [emailModalTargets, setEmailModalTargets] = useState<EmailTarget[] | null>(null)

  // PDF blob URL
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [showPdf, setShowPdf]       = useState(false)
  const prevPdfRef = useRef<string | null>(null)

  // XML-viewer geselecteerde regel
  const [selectedLine, setSelectedLine] = useState<number | null>(null)

  // Afgeleide waarden
  const selectedEntry  = entries.find(e => e.id === selectedId) ?? null
  const displayResult  = historyResult ?? selectedEntry?.result ?? null
  const displayFilename = historyFilename ?? selectedEntry?.name ?? null
  const displayFileSize = historyResult ? null : (selectedEntry?.size ?? null)
  const isEntryLoading = selectedEntry != null &&
    (selectedEntry.status === 'pending' || selectedEntry.status === 'validating')

  const completedCount = entries.filter(e => e.status === 'done' || e.status === 'error').length
  const hasFiles       = entries.length > 0
  const hasHistory     = historyResult != null

  // Ruim blob-URL op bij ontkoppelen
  useEffect(() => {
    return () => { if (prevPdfRef.current) URL.revokeObjectURL(prevPdfRef.current) }
  }, [])

  // Herbouw blob-URL als het geselecteerde resultaat wisselt
  useEffect(() => {
    if (prevPdfRef.current) { URL.revokeObjectURL(prevPdfRef.current); prevPdfRef.current = null }
    setPdfBlobUrl(null)
    setShowPdf(false)
    setSelectedLine(null)

    if (displayResult?.pdf_base64) {
      try {
        const binary = atob(displayResult.pdf_base64)
        const bytes  = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
        const blob = new Blob([bytes], { type: 'application/pdf' })
        const url  = URL.createObjectURL(blob)
        prevPdfRef.current = url
        setPdfBlobUrl(url)
      } catch { /* PDF-fout stilzwijgend negeren */ }
    }
  }, [displayResult])

  const emailErrorMessage = useCallback((err: unknown): string => {
    const code = err instanceof ValidateEmailError ? err.code : null
    switch (code) {
      case 'invalid_email':      return t('validator_page.email_error_invalid_email')
      case 'no_documents':       return t('validator_page.email_error_no_documents')
      case 'too_many_documents': return t('validator_page.email_error_too_many_documents')
      case 'file_too_large':     return t('validator_page.email_error_file_too_large')
      case 'empty_file':         return t('validator_page.email_error_empty_file')
      case 'unsupported_type':   return t('validator_page.email_error_unsupported_type')
      default: return err instanceof Error ? err.message : t('validator_page.unknown_error')
    }
  }, [t])

  const attachmentToEntry = useCallback((item: EmailAttachmentResult): ValidatorFileEntry => ({
    id: crypto.randomUUID(),
    kind: 'document',
    name: item.filename,
    size: item.file_size,
    status: item.status === 'ok' && item.result ? 'done' : 'error',
    result: item.result ?? undefined,
    errorMsg: item.status === 'ok' ? undefined
      : item.error_code === 'unreadable_attachment' ? t('validator_page.attachment_error_unreadable')
      : t('validator_page.attachment_error_validation_failed'),
  }), [t])

  const validateEmailEntry = useCallback(async (entry: ValidatorFileEntry) => {
    setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, status: 'validating' } : e))
    try {
      const res = await validateEmail(entry.file!)
      const docs = res.items.map(attachmentToEntry)
      setSkippedCount(n => n + res.skipped_count)
      if (res.depth_limit_reached) setDepthLimitReached(true)
      // Vervang de containermail door zijn bijlagen, op dezelfde positie.
      setEntries(prev => prev.flatMap(e => e.id === entry.id ? docs : [e]))
      const firstUsable = docs.find(d => d.status === 'done') ?? docs[0]
      setSelectedId(cur => (cur === entry.id && firstUsable ? firstUsable.id : cur))
    } catch (err) {
      setEntries(prev => prev.map(e => e.id === entry.id ? {
        ...e,
        status: 'error',
        errorMsg: emailErrorMessage(err),
      } : e))
    }
  }, [attachmentToEntry, emailErrorMessage])

  const validateOneEntry = useCallback(async (entry: ValidatorFileEntry) => {
    if (entry.kind === 'email') return validateEmailEntry(entry)
    setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, status: 'validating' } : e))
    try {
      const result = await validateXml(entry.file!)
      setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, status: 'done', result } : e))
    } catch (err) {
      setEntries(prev => prev.map(e => e.id === entry.id ? {
        ...e,
        status: 'error',
        errorMsg: err instanceof Error ? err.message : t('validator_page.unknown_error'),
      } : e))
    }
  }, [t, validateEmailEntry])

  const startBatchValidation = useCallback(async (toValidate: ValidatorFileEntry[]) => {
    for (let i = 0; i < toValidate.length; i += BATCH_SIZE) {
      await Promise.all(toValidate.slice(i, i + BATCH_SIZE).map(validateOneEntry))
    }
  }, [validateOneEntry])

  const handleDrop = useCallback((accepted: File[]) => {
    const validFiles = accepted.filter(f => {
      const name = f.name.toLowerCase()
      return SUPPORTED_EXTENSIONS.some(ext => name.endsWith(ext))
    })
    if (validFiles.length === 0) return

    setHistoryResult(null)
    setHistoryFilename(null)
    setSelectedLine(null)
    setEmailSelectedIds(new Set())
    setSkippedCount(0)
    setDepthLimitReached(false)

    const newEntries: ValidatorFileEntry[] = validFiles.map(f => ({
      id: crypto.randomUUID(),
      kind: isEmailContainer(f.name) ? 'email' as const : 'document' as const,
      name: f.name,
      size: f.size,
      file: f,
      status: 'pending' as FileStatus,
    }))

    setEntries(newEntries)
    setSelectedId(newEntries[0].id)
    startBatchValidation(newEntries)
  }, [startBatchValidation])

  const handleToggleEmailSelect = useCallback((id: string) => {
    setEmailSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleToggleSelectAll = useCallback(() => {
    const emailableIds = entries
      .filter(e => e.status === 'done' && e.result?.saved_session_id != null)
      .map(e => e.id)
    const allSelected = emailableIds.every(id => emailSelectedIds.has(id))
    setEmailSelectedIds(allSelected ? new Set() : new Set(emailableIds))
  }, [entries, emailSelectedIds])

  const handleOpenEmailModal = useCallback(() => {
    const targets: EmailTarget[] = entries
      .filter(e => emailSelectedIds.has(e.id) && e.result?.saved_session_id != null)
      .map(e => ({
        sessionId: e.result!.saved_session_id!,
        filename: e.name,
        standard: e.result!.standard,
        document_type: e.result!.document_type,
      }))
    if (targets.length > 0) setEmailModalTargets(targets)
  }, [entries, emailSelectedIds])

  const handleSendSingleEmail = useCallback(() => {
    if (!displayResult?.saved_session_id) return
    setEmailModalTargets([{
      sessionId: displayResult.saved_session_id,
      filename: displayFilename ?? 'bestand.xml',
      standard: displayResult.standard,
      document_type: displayResult.document_type,
    }])
  }, [displayResult, displayFilename])

  const handleReset = () => {
    setEntries([])
    setSelectedId(null)
    setHistoryResult(null)
    setHistoryFilename(null)
    setSelectedLine(null)
    setEmailSelectedIds(new Set())
    setSkippedCount(0)
    setDepthLimitReached(false)
  }

  const handleOpenFromHistory = (res: ValidateResult, filename: string) => {
    setEntries([])
    setSelectedId(null)
    setSkippedCount(0)
    setDepthLimitReached(false)
    setHistoryResult(res)
    setHistoryFilename(filename)
    setSelectedLine(null)
    setActiveTab('validate')
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      'text/xml': ['.xml'],
      'application/xml': ['.xml'],
      'application/pdf': ['.pdf'],
      'message/rfc822': ['.eml'],
      'application/vnd.ms-outlook': ['.msg'],
    },
    noClick: hasFiles || hasHistory,
  })

  return (
    <div className="space-y-4">

      {/* Tab-balk */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
        {([
          { id: 'validate', label: t('validator_page.tab_validate') },
          { id: 'history',  label: t('validator_page.tab_history') },
        ] as { id: Tab; label: string }[]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-700 dark:text-blue-300 bg-white dark:bg-gray-800'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Geschiedenis-tab ──────────────────────────────────────────── */}
      {activeTab === 'history' && <ValidatorHistoryPanel onOpen={handleOpenFromHistory} />}

      {/* E-mail modal */}
      {emailModalTargets && (
        <ValidatorEmailModal
          targets={emailModalTargets}
          onClose={() => setEmailModalTargets(null)}
        />
      )}

      {/* ── Valideren-tab ─────────────────────────────────────────────── */}
      {activeTab === 'validate' && (
        <div {...getRootProps()} className="relative outline-none">
          <input {...getInputProps()} />

          {/* Drag-over overlay (alleen wanneer al bestanden geladen zijn) */}
          {isDragActive && (hasFiles || hasHistory) && (
            <div className="absolute inset-0 z-20 border-2 border-dashed border-blue-500 bg-blue-50/80 rounded-xl flex items-center justify-center pointer-events-none">
              <p className="text-blue-700 font-semibold text-sm">{t('validator_page.drop_to_replace')}</p>
            </div>
          )}

          {/* Lege staat: grote dropzone */}
          {!hasFiles && !hasHistory && (
            <div className="max-w-2xl">
              <div className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}>
                <div className="text-3xl mb-3 text-gray-300">📂</div>
                {isDragActive
                  ? <p className="text-blue-600 dark:text-blue-300 font-medium">{t('validator_page.drop_files_active')}</p>
                  : <>
                      <p className="text-gray-500 dark:text-gray-400">{t('validator_page.drop_files_idle')} <span className="text-blue-600 underline">{t('validator_page.choose_files')}</span></p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">{t('validator_page.supported_formats')}</p>
                    </>
                }
              </div>
            </div>
          )}

          {/* Gevulde staat: sidebar + detailpaneel */}
          {(hasFiles || hasHistory) && (
            <div className="flex gap-4 items-start">

              {/* Sidebar (alleen bij meerdere bestanden) */}
              {hasFiles && entries.length > 1 && (
                <ValidatorFileSidebar
                  entries={entries}
                  selectedId={selectedId}
                  completedCount={completedCount}
                  onSelect={id => { setSelectedId(id); setSelectedLine(null) }}
                  onReset={handleReset}
                  emailSelectedIds={emailSelectedIds}
                  onToggleEmailSelect={handleToggleEmailSelect}
                  onToggleSelectAll={handleToggleSelectAll}
                  onSendEmail={handleOpenEmailModal}
                />
              )}

              {/* Detailpaneel */}
              <div className="flex-1 min-w-0 space-y-4">

                {/* Compacte balk voor enkel bestand of history */}
                {(entries.length === 1 || hasHistory) && (
                  <div className="flex items-center gap-3 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800">
                    <span className="text-gray-300 shrink-0 text-sm">📎</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate min-w-0">
                      {isEntryLoading ? t('validator_page.validating_busy') : (displayFilename ?? '')}
                    </span>
                    {isEntryLoading && (
                      <svg className="animate-spin h-4 w-4 text-blue-500 shrink-0" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                    )}
                    {displayFileSize != null && !isEntryLoading && (
                      <span className="text-xs text-gray-400 shrink-0">
                        {Math.round(displayFileSize / 1024)} KB
                      </span>
                    )}
                    {displayResult?.saved_session_id != null && !isEntryLoading && (
                      <button
                        onClick={handleSendSingleEmail}
                        className="shrink-0 flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 border border-blue-200 dark:border-blue-800/50 hover:border-blue-400 px-2.5 py-1 rounded-lg bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        title={t('validator_page.send_email_title')}
                      >
                        ✉ {t('validator_page.send_email_button')}
                      </button>
                    )}
                    <button
                      onClick={handleReset}
                      className="ml-auto shrink-0 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {t('validator_page.new_set')}
                    </button>
                  </div>
                )}

                {/* Overgeslagen mailbijlagen (niet-XML/PDF) */}
                {hasFiles && (skippedCount > 0 || depthLimitReached) && (
                  <p role="status" className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 px-1">
                    <span aria-hidden="true">ⓘ</span>
                    {skippedCount > 0 && t('validator_page.skipped_attachments', { count: skippedCount })}
                    {skippedCount > 0 && depthLimitReached && ' · '}
                    {depthLimitReached && t('validator_page.depth_limit_reached')}
                  </p>
                )}

                {/* Laadstatus voor geselecteerd bestand */}
                {isEntryLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 px-1">
                    <svg className="animate-spin h-4 w-4 text-blue-400" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    {t('validator_page.validating_busy')}
                  </div>
                )}

                {/* Fout bij geselecteerd bestand */}
                {selectedEntry?.status === 'error' && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 rounded-xl px-4 py-3 text-sm">
                    {selectedEntry.errorMsg ?? t('validator_page.unknown_error')}
                  </div>
                )}

                {/* Resultaatdetail */}
                {displayResult && !isEntryLoading && (
                  <ValidatorResultDetail
                    result={displayResult}
                    filename={displayFilename}
                    fileSize={displayFileSize}
                    pdfBlobUrl={pdfBlobUrl}
                    showPdf={showPdf}
                    onTogglePdf={() => setShowPdf(p => !p)}
                    selectedLine={selectedLine}
                    onSelectLine={setSelectedLine}
                  />
                )}

                {/* Geen bestand geselecteerd (multi-file, nog niets gekozen) */}
                {!displayResult && !isEntryLoading && !selectedEntry?.errorMsg && hasFiles && (
                  <div className="text-sm text-gray-400 dark:text-gray-500 px-1">
                    {t('validator_page.select_file_prompt')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
